import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LoginScreen } from './components/LoginScreen';
import { NavBar, type Route } from './components/nav/NavBar';
import { PageHeader } from './components/layout/PageHeader';
import { HomePage } from './pages/HomePage';
import { fetchSnapshot } from './api/snapshot';
import { fetchHistory } from './api/history';
import { fetchHealth } from './api/health';
import { fetchPvForecast } from './api/pvForecast';
import { fetchSocForecast } from './api/socForecast';
import { fetchBmsConditioning } from './api/bmsConditioning';
import { fetchPrices } from './api/prices';
import { fetchSavingsTotal } from './api/savings';
import { fetchInstallation } from './api/installation';
import { fetchAuthStatus, logout as authLogout } from './api/auth';
import { fetchReserveLock } from './api/reserveLock';
import { ApiError } from './api/client';
import { usePolling, isPollingStale } from './hooks/usePolling';
import { computeNowIdx, STEP_MIN } from './lib/time';
import { RequestPriority } from './lib/requestScheduler';
import { clearAuthHint, readAuthHint, writeAuthHint } from './lib/authHint';

// Settings en Tech worden pas geladen als je er heen navigeert. Ze zaten in de
// chunk die iedere bezoeker binnenhaalt, terwijl een bewoner zonder
// beheerdersrol die routes niet eens mag zien.
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const TechPage = lazy(() =>
  import('./pages/TechPage').then((m) => ({ default: m.TechPage })),
);

// De prefix waaronder deze build wordt geserveerd. Niet hardcoderen op '/v2/':
// dezelfde bron wordt ook onder een andere prefix gebouwd om twee versies
// naast elkaar te kunnen draaien, en een vaste prefix stuurt dan elke
// navigatie naar de verkeerde plek.
const BASE = import.meta.env.BASE_URL || '/';

function routeUrl(r: Route): string {
  return r === 'home' ? BASE : `${BASE}${r}`;
}

// De fallbacks houden de paginastructuur aan die de echte pagina bij het laden
// óók toont: dezelfde container-class en dezelfde kop, zodat er niets
// verspringt zodra de chunk binnen is.
function SettingsFallback() {
  return (
    <div className="settings-page">
      <PageHeader
        title="Site instellingen"
        hint={
          <>
            Wijzigingen schrijven naar <code>config/dashboard-overrides.yaml</code> en zijn
            actief na service-restart.
          </>
        }
      />
      <div className="panel-card">
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Instellingen laden…</p>
      </div>
    </div>
  );
}

function TechFallback() {
  return (
    <div className="tech-page">
      <PageHeader
        title="Systeem &amp; diagnostiek"
        hint={
          <>
            Read-only overzicht van services, registers en EMS-events.
            Wijzigingen aan config: zie <code>{`${BASE}settings`}</code>.
          </>
        }
      />
    </div>
  );
}

// PR-2 refactor (H9 audit-2026-05-13): App.tsx is nu een routing-shell.
// - Auth-gate (LoginScreen vs dashboard)
// - Route-state via pathname (window.location.pathname → 'home'|'settings'|'tech')
// - Polling-fetches blijven hier zodat alle pages dezelfde freshness delen
//   (HomePage/Tech kunnen beide snapshot lezen zonder dubbele polling)
// - Body-rendering gedelegeerd aan pages/HomePage|SettingsPage|TechPage

function readRouteFromPath(): Route {
  const prefix = BASE.replace(/\/$/, '');
  let rest = window.location.pathname;
  if (prefix && rest.startsWith(prefix)) rest = rest.slice(prefix.length);
  const p = rest.replace(/^\//, '').split('/')[0];
  if (p === 'settings') return 'settings';
  if (p === 'tech') return 'tech';
  return 'home';
}

export default function App() {
  // Auth-state: undefined = nog aan het checken; false = login tonen;
  // true = dashboard render. usePolling start pas met data-fetches als
  // user authed is — voorkomt 401-spam in console.
  // Dev-bypass: zet VITE_DEV_NO_AUTH=1 in .env.development.local om de
  // auth-gate te skippen tijdens lokale UI-iteratie. Combineer met de Vite
  // proxy die X-API-Key header injecteert (zie vite.config.ts) → backend
  // accepteert calls zonder cookie-sessie. Productie raakt dit niet aan.
  const DEV_NO_AUTH = import.meta.env.VITE_DEV_NO_AUTH === '1';
  const [authed, setAuthed] = useState<boolean | undefined>(
    DEV_NO_AUTH ? true : undefined,
  );
  // Perf 2026-08-11: de sessie-cookie is HttpOnly, dus tot /api/auth/status
  // terug was (0,3-2,8 s) startte er geen enkele fetch en bleef het scherm
  // wit. De hint uit localStorage zegt of je hier de vorige keer ingelogd
  // was; is dat zo, dan beginnen de fetches meteen, parallel aan de
  // status-call. De server beoordeelt elke request nog steeds zelf — klopt de
  // hint niet meer, dan geven ze 401 en verschijnt alsnog het loginscherm.
  // Eén keer bij mount lezen: later wisselen zou de fetches midden in de rit
  // aan- of uitzetten.
  const authHint = useMemo(
    () => (DEV_NO_AUTH ? null : readAuthHint()),
    [DEV_NO_AUTH],
  );
  // v36 (2026-07-25): multi-user auth — track admin-flag zodat Settings/Tech
  // routes gate'ed kunnen worden. DEV_NO_AUTH bypass = admin (voorheen enige
  // gebruikersvorm).
  const [isAdmin, setIsAdmin] = useState<boolean>(
    DEV_NO_AUTH || authHint?.admin === true,
  );
  // Is er in deze paginasessie ooit een bevestigde login geweest? Bepaalt of
  // het loginscherm de sitenaam mag tonen: na uitloggen wél (dat deed het
  // altijd al), bij een koude start met alleen een hint niet — anders zou een
  // bezoeker die niet ingelogd raakt ineens de sitenaam zien.
  const confirmedAuthRef = useRef(false);

  const [route, setRoute] = useState<Route>(() => readRouteFromPath());

  function navigate(r: Route) {
    setRoute(r);
    const url = routeUrl(r);
    window.history.pushState({ route: r }, '', url);
  }

  useEffect(() => {
    const onPop = () => setRoute(readRouteFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // v36 (2026-07-25): non-admin users mogen alleen /home benaderen.
  // Directe URL-navigatie naar /settings of /tech wordt terug-gerouteerd
  // naar /home. NavBar verbergt de links ook — dit is de defensieve laag
  // voor deep-links + browser back/forward.
  useEffect(() => {
    if (authed === true && !isAdmin && route !== 'home') {
      setRoute('home');
      window.history.replaceState({ route: 'home' }, '', routeUrl('home'));
    }
  }, [authed, isAdmin, route]);

  useEffect(() => {
    if (DEV_NO_AUTH) return; // bypass — Vite proxy injecteert X-API-Key
    let cancelled = false;
    fetchAuthStatus()
      .then((s) => {
        if (cancelled) return;
        setAuthed(s.authed);
        // v36: is_admin bepaalt of Settings/Tech routes zichtbaar zijn.
        // Backward-compat: als backend geen is_admin retourneert (oudere Pi),
        // treat als false — non-admin default is veiliger dan admin default.
        setIsAdmin(Boolean(s.is_admin));
        if (s.authed) writeAuthHint(Boolean(s.is_admin));
        else clearAuthHint();
      })
      // Netwerkfout: de hint blijft staan. Dat is geen uitspraak over de
      // sessie, alleen over het feit dat we het niet konden vragen.
      .catch(() => { if (!cancelled) setAuthed(false); });
    return () => { cancelled = true; };
  }, [DEV_NO_AUTH]);

  useEffect(() => {
    if (authed === true) confirmedAuthRef.current = true;
  }, [authed]);

  // Fetches lopen zodra we authed zijn, én alvast tijdens het wachten op
  // /api/auth/status als de hint zegt dat dat kansrijk is.
  const fetchEnabled = authed === true || (authed === undefined && authHint !== null);

  // Polling — alleen actief als user authed is. Page-componenten consumeren
  // de data via props (zie HomePage), zodat we ze ook in Tech kunnen hergebruiken
  // zonder dubbele fetches. Reads gebruiken sinds H9 same-origin cookie via
  // credentials: 'include' (zie api/client.ts).
  const { data: snap, error: snapErr } = usePolling(fetchSnapshot, 5_000, { enabled: fetchEnabled, priority: RequestPriority.snapshot });
  const healthPoll = usePolling(fetchHealth, 30_000, { enabled: fetchEnabled, priority: RequestPriority.health });
  // #202: bij een mislukte health-poll houdt usePolling de vorige payload vast.
  // Die mag dan niet meer als actuele stand getoond worden ("Optimaal" bleef
  // anders onbeperkt staan). Drie gemiste pollcycli = verouderd.
  const healthStale = isPollingStale(healthPoll, 3 * 30_000);
  const health = healthStale ? null : healthPoll.data;
  const { data: pvF }     = usePolling(fetchPvForecast, 300_000, { enabled: fetchEnabled, priority: RequestPriority.pvForecast });
  const { data: socF }    = usePolling(fetchSocForecast, 300_000, { enabled: fetchEnabled, priority: RequestPriority.socForecast });
  const { data: bmsCond } = usePolling(fetchBmsConditioning, 300_000, { enabled: fetchEnabled, priority: RequestPriority.secondary });
  const { data: prices }  = usePolling(fetchPrices, 300_000, { enabled: fetchEnabled, priority: RequestPriority.prices });
  const { data: savingsTotal } = usePolling(fetchSavingsTotal, 600_000, { enabled: fetchEnabled, priority: RequestPriority.savings });
  const { data: install }     = usePolling(fetchInstallation, 3_600_000, { enabled: fetchEnabled, priority: RequestPriority.installation });
  const { data: reserveResp } = usePolling(fetchReserveLock, 60_000, { enabled: fetchEnabled, priority: RequestPriority.controls });
  const reserveLock = reserveResp?.lock ?? null;

  // Een 401 op een datacall betekent dat de sessie weg is — verlopen, ingetrokken,
  // of nooit blijven plakken. Dat hoort het loginscherm te openen.
  //
  // Aanleiding (2026-08-28, issue #425): Bram kreeg op het LAN-adres de rode regel
  // "Geen data beschikbaar: /api/snapshot: 401" onder een volledige navigatiebalk
  // met een Uitloggen-knop. Dat leest als een storing van de site, en hij meldde de
  // Pi als down terwijl de regellus gewoon draaide. Een 401 is geen datafout maar
  // een auth-fout, en de enige zinnige uitweg is opnieuw inloggen.
  useEffect(() => {
    if (snapErr instanceof ApiError && snapErr.status === 401) {
      clearAuthHint();
      setAuthed(false);
    }
  }, [snapErr]);

  // Adaptive-horizon (2026-05-28): "Nu"-marker slides right when the forecast
  // is short, exposing more history on the left so the right edge of the chart
  // doesn't go empty before day-ahead prices arrive. Compute the effective
  // nowIdx from the STRATEGY-DRIVING forecast sources only (prices +
  // soc-forecast). PV-forecast is excluded on purpose: it routinely extends
  // 48h ahead via forecast.solar/open-meteo and would otherwise dominate the
  // MAX → "Nu" never shifts (the right-edge empty space the user complains
  // about is missing PRICES and SOC, not missing PV). We take the MIN of the
  // strategy sources so the marker tracks whichever signal runs out first —
  // that's the bucket from which the chart visibly "goes empty" for the user.
  const nowIdx = useMemo(() => {
    const lastPrice = prices?.prices?.length
      ? new Date(prices.prices[prices.prices.length - 1].hour_start).getTime() + 60 * 60 * 1000
      : null;
    const lastSocF = socF?.trajectory?.length
      ? new Date(socF.trajectory[socF.trajectory.length - 1].hour_start).getTime() + 60 * 60 * 1000
      : null;
    // MIN over available strategy-end-times. computeNowIdx itself treats the
    // input list as a MAX-set, so we collapse to the single MIN value here.
    const strategicEnds = [lastPrice, lastSocF].filter((t): t is number => t != null);
    if (strategicEnds.length === 0) {
      return computeNowIdx(new Date(), []);
    }
    const limitingEnd = Math.min(...strategicEnds);
    return computeNowIdx(new Date(), [new Date(limitingEnd)]);
  }, [prices, socF]);

  // History hours = how many past hours we need to fully populate the chart
  // from idx=0 up to nowIdx. Round up + small margin so the leftmost bucket is
  // not half-empty due to bucket-rounding. Capped at 48 by the server.
  const historyHours = useMemo(() => {
    const baseH = Math.ceil((nowIdx * STEP_MIN) / 60);
    return Math.max(24, Math.min(48, baseH + 1));
  }, [nowIdx]);

  // Wrap fetchHistory so usePolling captures the latest historyHours via the
  // ref-update pattern. Forecasts refresh every 5 min, so historyHours can
  // shift at most a handful of times per day — well within the polling rate.
  const fetchHistoryAdaptive = useCallback(
    () => fetchHistory(historyHours),
    [historyHours],
  );
  // refetchKey: bij mount zijn de prijzen er nog niet, dus nowIdx staat op de
  // default en de eerste fetch vraagt 25 uur. Zodra de prijzen binnen zijn
  // groeit historyHours (tot ~37); dan meteen opnieuw halen, niet pas na 60 s.
  const { data: hist } = usePolling(fetchHistoryAdaptive, 60_000, {
    enabled: fetchEnabled, priority: RequestPriority.history, refetchKey: historyHours,
  });

  useEffect(() => {
    document.title = install?.site_name ? `ZBS - ${install.site_name}` : 'ZBS';
  }, [install?.site_name]);

  // Route → body-class zodat global.css de body voor Settings/Tech wél laat
  // scrollen (HomePage blijft no-scroll om hero-row + chart-row exact in
  // viewport te houden). Volledige reset bij unmount.
  useEffect(() => {
    document.body.classList.toggle('route-home', route === 'home');
    document.body.classList.toggle('route-settings', route === 'settings');
    document.body.classList.toggle('route-tech', route === 'tech');
    return () => {
      document.body.classList.remove('route-home', 'route-settings', 'route-tech');
    };
  }, [route]);

  async function handleLogout() {
    try {
      await authLogout();
    } finally {
      clearAuthHint();
      setAuthed(false);
    }
  }

  // Auth-gate: blokkeer dashboard tot we weten dat user authed is.
  // Zonder hint blijft dat een korte blackout — dat is een bezoeker die hier
  // niet eerder ingelogd was, en die krijgt zo geen dashboard te zien dat
  // meteen weer plaatsmaakt voor het loginscherm. Mét hint tonen we de
  // normale layout met lege waarden; dat is exact het beeld dat er ook nu al
  // staat op het moment dat de auth-check klaar is en de data nog onderweg.
  if (authed === undefined && authHint === null) {
    return null;
  }
  if (authed === false) {
    return (
      <LoginScreen
        siteLabel={confirmedAuthRef.current ? (install?.site_name ?? undefined) : undefined}
        onSuccess={(result) => {
          setAuthed(true);
          // v36: bewaar admin-flag uit login-response zodat de UI
          // Settings/Tech direct kan gate'en zonder tweede status-fetch.
          setIsAdmin(Boolean(result?.is_admin));
          writeAuthHint(Boolean(result?.is_admin));
        }}
      />
    );
  }


  // Snapshot-fout op de Home-pagina is fataal voor de hero-row (Energiestroom
  // SVG kan niets zonder snap). Op Settings/Tech is het niet nodig: die hebben
  // hun eigen data-bronnen (config-effective, services, etc.).
  // `authed === true` erbij: tijdens het wachten op de auth-check kan een
  // verlopen sessie een 401 op de snapshot geven. Die foutmelding hoort de
  // gebruiker niet te zien — een tel later staat het loginscherm er. De 401 zelf
  // wordt hierboven al omgezet in `authed === false`; deze tak is dus alleen nog
  // voor échte datafouten (500, netwerk, kapotte payload).
  const snapErrIsAuth = snapErr instanceof ApiError && snapErr.status === 401;
  if (snapErr && !snapErrIsAuth && route === 'home' && authed === true) {
    return (
      <>
        <NavBar
          current={route}
          onNavigate={navigate}
          siteLabel={install?.site_name ?? undefined}
          onLogout={handleLogout}
          isAdmin={isAdmin}
        />
        <div style={{ padding: 24, color: '#b91c1c' }}>
          Geen data beschikbaar: {snapErr.message}
        </div>
      </>
    );
  }

  return (
    <>
      {/* isAdmin hoort hier net zo goed als in de snapErr-tak hierboven. Zonder
          deze prop won de default van NavBar en zag een bewoner de Settings- en
          Tech-links staan; de route-guard hierboven stuurde hem daarna terug
          naar home. Een link die je terugkaatst is dezelfde soort storing als
          een knop die 403 geeft. */}
      <NavBar
        current={route}
        onNavigate={navigate}
        siteLabel={install?.site_name ?? undefined}
        onLogout={handleLogout}
        isAdmin={isAdmin}
      />
      {route === 'home' && (
        // Dashboard v3 (cycle #22): Solis-style FlowSVG + 3×5 grid layout,
        // FlowSVG sidebar links op desktop / hero-band bovenaan op mobiel.
        // Hard-cutover — geen A/B feature-flag (Bram-decision 2026-05-17).
        // Oude HomePage v2 staat nog in repo als historisch artifact.
        <HomePage
          snap={snap}
          hist={hist}
          health={health}
          healthStale={healthStale}
          pvF={pvF}
          socF={socF}
          bmsCond={bmsCond}
          prices={prices}
          savingsTotal={savingsTotal}
          install={install}
          reserveLock={reserveLock}
          nowIdx={nowIdx}
          onLogout={handleLogout}
          isAdmin={isAdmin}
        />
      )}
      {route === 'settings' && (
        <Suspense fallback={<SettingsFallback />}>
          <SettingsPage />
        </Suspense>
      )}
      {route === 'tech' && (
        <Suspense fallback={<TechFallback />}>
          <TechPage />
        </Suspense>
      )}
    </>
  );
}
